import { useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Overlay'
import { TagEditor } from '@/components/review/TagEditor'
import { ACCEPTED_IMAGE, ACCEPTED_VIDEO, MAX_UPLOAD_BYTES } from '@/lib/constants'
import { bytes } from '@/lib/format'

export interface PendingUpload {
  name: string
  size: number
  mediaType: 'image' | 'video'
  url: string
  description: string
  tags: string[]
  attribution: string
}

/**
 * A modal, not a drawer: uploading is a multi-step action, not a record edit
 * (docs/03-page-specs.md §7.3).
 *
 * Validation runs before any bytes move, and the error names the offending
 * file. In production the accepted files go **direct to S3 via a presigned
 * URL**, never through the API — here they become object URLs.
 * Blocked on docs/00-overview.md §5.2 for the real storage path, the max
 * size, and the video codec/poster rules.
 */
export function UploadModal({
  open,
  tagPool,
  onClose,
  onUpload,
}: {
  open: boolean
  tagPool: string[]
  onClose: () => void
  onUpload: (files: PendingUpload[]) => Promise<void>
}) {
  const [queue, setQueue] = useState<PendingUpload[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  function accept(files: FileList | null) {
    if (!files) return
    const nextErrors: string[] = []
    const nextQueue: PendingUpload[] = []

    Array.from(files).forEach((file) => {
      const isImage = ACCEPTED_IMAGE.includes(file.type)
      const isVideo = ACCEPTED_VIDEO.includes(file.type)
      if (!isImage && !isVideo) {
        nextErrors.push(`${file.name} is a ${file.type || 'unknown'} file, which is not accepted.`)
        return
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        nextErrors.push(
          `${file.name} is ${bytes(file.size)}, over the ${bytes(MAX_UPLOAD_BYTES)} limit.`,
        )
        return
      }
      nextQueue.push({
        name: file.name,
        size: file.size,
        mediaType: isVideo ? 'video' : 'image',
        url: URL.createObjectURL(file),
        description: '',
        tags: [],
        attribution: '',
      })
    })

    setErrors(nextErrors)
    setQueue((prev) => [...prev, ...nextQueue])
  }

  function reset() {
    setQueue([])
    setErrors([])
    onClose()
  }

  /** Description is required — it is what the agent searches on. */
  const ready = queue.length > 0 && queue.every((f) => f.description.trim().length > 0)

  return (
    <Modal
      open={open}
      onClose={reset}
      title="Add media"
      description="Uploaded media is stored by the portal and approved on save. Give every file a description — that is what the agent searches on."
      width="max-w-[680px]"
      footer={
        <>
          <Button size="sm" onClick={reset}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!ready}
            loading={busy}
            onClick={async () => {
              setBusy(true)
              try {
                await onUpload(queue)
                setQueue([])
                setErrors([])
              } finally {
                setBusy(false)
              }
            }}
          >
            Upload {queue.length || ''}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => input.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            accept(e.dataTransfer.files)
          }}
          className="flex w-full flex-col items-center gap-2 rounded-[6px] border border-dashed border-rule-strong bg-canvas px-4 py-8 text-center hover:border-brand-blue hover:bg-brand-blue-50"
        >
          <Upload size={20} className="text-ink-faint" aria-hidden />
          <span className="font-head text-[13px] font-semibold">
            Drop files here, or choose from your computer
          </span>
          <span className="text-[12px] text-ink-faint">
            JPG, PNG, WebP, GIF, MP4, WebM, MOV · up to {bytes(MAX_UPLOAD_BYTES)} each
          </span>
        </button>

        <input
          ref={input}
          type="file"
          multiple
          hidden
          accept={[...ACCEPTED_IMAGE, ...ACCEPTED_VIDEO].join(',')}
          onChange={(e) => accept(e.target.files)}
        />

        {errors.length > 0 && (
          <ul className="space-y-1 rounded-[4px] bg-brand-red-50 px-3 py-2">
            {errors.map((err) => (
              <li key={err} className="text-[13px] leading-5 text-brand-red-700">
                {err}
              </li>
            ))}
          </ul>
        )}

        {queue.length > 0 && (
          <ul className="max-h-[320px] space-y-3 overflow-y-auto">
            {queue.map((file, i) => (
              <li key={`${file.name}-${i}`} className="rounded-[6px] border border-rule p-3">
                <div className="flex items-baseline gap-2">
                  <span className="truncate font-head text-[13px] font-semibold">{file.name}</span>
                  <span className="tabular ml-auto shrink-0 text-[11px] text-ink-faint">
                    {file.mediaType} · {bytes(file.size)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQueue((q) => q.filter((_, j) => j !== i))}
                    className="text-[12px] text-brand-red-700 hover:underline"
                  >
                    Remove
                  </button>
                </div>
                <div className="mt-2 space-y-2">
                  <Input
                    value={file.description}
                    placeholder="Describe what is in the frame"
                    aria-label={`Description for ${file.name}`}
                    className="h-8 text-[13px]"
                    onChange={(e) =>
                      setQueue((q) =>
                        q.map((f, j) => (j === i ? { ...f, description: e.target.value } : f)),
                      )
                    }
                  />
                  <Input
                    value={file.attribution}
                    placeholder="Attribution (optional)"
                    aria-label={`Attribution for ${file.name}`}
                    className="h-8 text-[13px]"
                    onChange={(e) =>
                      setQueue((q) =>
                        q.map((f, j) => (j === i ? { ...f, attribution: e.target.value } : f)),
                      )
                    }
                  />
                  <TagEditor
                    tags={file.tags}
                    suggestions={tagPool}
                    onChange={(tags) =>
                      setQueue((q) => q.map((f, j) => (j === i ? { ...f, tags } : f)))
                    }
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  )
}
